import { zodResolver } from "@hookform/resolvers/zod";
import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import z from "zod";
import AppInputField from "../../components/ui/AppInputField";
import { Modal } from "../../components/ui/Modal";
import SuccessView from "../../components/ui/SuccessView";
import { useToast } from "../../hooks/useToast";
import {
  useCreateRoleMutation,
  useGetAllPermissionsQuery,
} from "./userApiSlice";

export const addRoleSchema = z.object({
  name: z.string().nonempty("Role is required"),
  description: z.string().optional(),
  permissions: z
    .array(
      z.object({
        resourceName: z.string(),
        actions: z.array(z.string()),
      })
    )
    .min(1, "At least one permission must be selected"),
});

export type AddRoleForm = z.infer<typeof addRoleSchema>;

interface Permission {
  resourceName: string;
  actions: string[];
}

interface ExistingRole {
  roleId: string;
  name: string;
  description?: string;
  permissions: Permission[];
}

interface AddRoleProps {
  isOpen: boolean;
  onClose: () => void;
  existingRole?: ExistingRole | null;
  mode?: "add" | "edit";
}

const AddRoleModal: React.FC<AddRoleProps> = ({
  isOpen,
  onClose,
  existingRole = null,
  mode = "add",
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [currentTab, setCurrentTab] = useState("Dashboard");
  const [selectedPermissions, setSelectedPermissions] = useState<
    Record<string, string[]>
  >({});

  const toast = useToast();

  const [addRole, { reset: resetCreate }] = useCreateRoleMutation();

  const [updateRole, { reset: resetUpdate }] = useCreateRoleMutation();

  const { data: permissionsData, isLoading: isLoadingPermissions } =
    useGetAllPermissionsQuery();

  const availablePermissions = useMemo(() => {
    return permissionsData?.data?.permissions || [];
  }, [permissionsData?.data?.permissions]);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
  } = useForm<AddRoleForm>({
    resolver: zodResolver(addRoleSchema),
    mode: "onChange",
    defaultValues: {
      name: "",
      description: "",
      permissions: [],
    },
  });

  useEffect(() => {
    if (existingRole && mode === "edit") {
      setValue("name", existingRole.name);
      setValue("description", existingRole.description || "");

      const initialPermissions: Record<string, string[]> = {};
      existingRole.permissions.forEach((perm) => {
        initialPermissions[perm.resourceName] = perm.actions;
      });
      setSelectedPermissions(initialPermissions);
      setValue("permissions", existingRole.permissions);
    }
  }, [existingRole, mode, setValue]);

  useEffect(() => {
    if (!isOpen) {
      reset();
      setSelectedPermissions({});
      setCurrentTab("Dashboard");
      setShowSuccess(false);
      resetCreate();
      resetUpdate();
    }
  }, [isOpen, reset, resetCreate, resetUpdate]);

  const handlePermissionToggle = (resourceName: string, action: string) => {
    setSelectedPermissions((prev) => {
      const current = prev[resourceName] || [];
      const isSelected = current.includes(action);

      let updated: string[];
      if (isSelected) {
        updated = current.filter((a) => a !== action);
      } else {
        updated = [...current, action];
      }

      const newPermissions = {
        ...prev,
        [resourceName]: updated,
      };

      const formPermissions = Object.entries(newPermissions)
        .filter(([_, actions]) => actions.length > 0)
        .map(([resourceName, actions]) => ({
          resourceName,
          actions,
        }));

      setValue("permissions", formPermissions);

      return newPermissions;
    });
  };

  const isPermissionSelected = (
    resourceName: string,
    action: string
  ): boolean => {
    return selectedPermissions[resourceName]?.includes(action) || false;
  };

  const tabs = useMemo(() => {
    return availablePermissions.map((p: any) => p.resourceName);
  }, [availablePermissions]);

  const currentTabPermissions = useMemo(() => {
    return availablePermissions.find((p: any) => p.resourceName === currentTab);
  }, [availablePermissions, currentTab]);

  const formatActionName = (action: string): string => {
    return (
      action
        .split(".")
        .pop()
        ?.replace(/([A-Z])/g, " $1")
        .trim() || action
    );
  };

  const onSubmit = async (data: AddRoleForm) => {
    try {
      setIsSubmitting(true);

      const payload = {
        ...(existingRole && { roleId: existingRole.roleId }),
        name: data.name,
        description: data.description || "",
        permissions: data.permissions,
      };

      if (mode === "edit" && existingRole) {
        await updateRole(payload).unwrap();
        toast.success({
          title: "Success",
          message: "Role updated successfully",
        });
      } else {
        await addRole(payload).unwrap();
        toast.success({
          title: "Success",
          message: "Role created successfully",
        });
      }

      setShowSuccess(true);
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error: any) {
      toast.error(
        error?.data?.message ||
          `Failed to ${mode === "edit" ? "update" : "create"} role`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      reset();
      setSelectedPermissions({});
      setCurrentTab("Dashboard");
      setShowSuccess(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={
        showSuccess ? "" : `${mode === "edit" ? "Update" : "Add"} permission`
      }
      className="max-w-full sm:max-w-[80vw] lg:max-w-[50vw]"
    >
      {showSuccess ? (
        <SuccessView
          handleCloseModal={handleClose}
          heading={`Role ${
            mode === "edit" ? "updated" : "created"
          } successfully!`}
          message={`The role has been ${
            mode === "edit" ? "updated" : "created"
          } with the selected permissions.`}
        />
      ) : (
        <div className="space-y-6 p-8 overflow-y-auto">
          {/* Role Input */}
          <div>
            <AppInputField
              label="Role"
              type="text"
              error={errors.name?.message}
              {...(control.register ? control.register("name") : {})}
              className="bg-[#F6F6F6] border-[#F6F6F6] h-[48px]"
              placeholder="Enter role name"
            />
          </div>

          {/* Permission Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-4">
              Permission
            </label>

            {/* Tabs */}
            <div className="border-b border-gray-200 mb-6">
              <div className="relative">
                {/* Left scroll button */}
                <button
                  type="button"
                  onClick={() => {
                    const container = document.getElementById("tabs-container");
                    if (container) {
                      container.scrollLeft -= 200;
                    }
                  }}
                  className="absolute left-0 top-0 z-10 h-full bg-white border-r border-gray-200 px-2 hover:bg-gray-50 transition-colors"
                  disabled={isSubmitting}
                >
                  <svg
                    className="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>

                {/* Scrollable tabs container */}
                <div
                  id="tabs-container"
                  className="flex space-x-0 overflow-x-hidden scroll-smooth pl-10 pr-10"
                  style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
                >
                  {tabs.map((tab: string) => (
                    <button
                      key={tab}
                      type="button"
                      onClick={() => setCurrentTab(tab)}
                      className={`px-6 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                        currentTab === tab
                          ? "text-gray-900 border-gray-900"
                          : "text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300"
                      }`}
                      disabled={isSubmitting}
                    >
                      {tab}
                    </button>
                  ))}
                </div>

                {/* Right scroll button */}
                <button
                  type="button"
                  onClick={() => {
                    const container = document.getElementById("tabs-container");
                    if (container) {
                      container.scrollLeft += 200;
                    }
                  }}
                  className="absolute right-0 top-0 z-10 h-full bg-white border-l border-gray-200 px-2 hover:bg-gray-50 transition-colors"
                  disabled={isSubmitting}
                >
                  <svg
                    className="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Permission Checkboxes */}
            <div className="space-y-4 min-h-[220px] overflow-y-auto">
              {currentTabPermissions?.actions.map((action: string) => (
                <div key={action} className="flex items-center justify-between">
                  <span className="text-gray-700 font-medium">
                    {formatActionName(action)}
                  </span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={isPermissionSelected(currentTab, action)}
                      onChange={() =>
                        handlePermissionToggle(currentTab, action)
                      }
                      disabled={isSubmitting}
                    />
                    <div
                      className={`w-6 h-6 border-2 rounded-md flex items-center justify-center transition-colors ${
                        isPermissionSelected(currentTab, action)
                          ? "bg-red-600 border-red-600"
                          : "border-gray-300 bg-white hover:border-gray-400"
                      }`}
                    >
                      {isPermissionSelected(currentTab, action) && (
                        <svg
                          className="w-4 h-4 text-white"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                      )}
                    </div>
                  </label>
                </div>
              ))}
            </div>

            {errors.permissions && (
              <p className="mt-2 text-sm text-red-600">
                {errors.permissions.message}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-6">
            <button
              type="button"
              onClick={handleClose}
              className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit(onSubmit)}
              disabled={isSubmitting || isLoadingPermissions}
              className="px-6 py-3 bg-primary text-white rounded-lg font-medium hover:bg-primary/80 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              {isSubmitting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {mode === "edit" ? "Updating..." : "Adding..."}
                </>
              ) : (
                <>
                  <span>+</span>
                  <span>
                    {mode === "edit" ? "Update permission" : "Add permission"}
                  </span>
                </>
              )}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default AddRoleModal;
